import {createCoreSagas} from '../../../redux/factories/crud/createSagas';
import {actions, actionTypes} from './reducers';
import {apiClient} from '../../../shared/api';
import {call, put} from 'redux-saga/effects';
import {notifyError, notifySuccess} from '../../../shared/notificationUtils';
import {select} from 'redux-saga-test-plan/matchers';
import {
    ANNOTATION_TYPE_KEYWORD,
    ANNOTATION_TYPE_LOCATION,
    ANNOTATION_TYPE_REGEX,
    TEMPLATE_NAME_MIN_CHARS
} from '../constants/template';
import shortid from 'shortid';
import {concat, equals, gte, insert} from 'ramda';


const getTemplateState = state => state.TemplateCore;


function* detectBoundaries({payload}) {
    const {file, session} = payload;
    const formData = new FormData();
    formData.append('image', file, file.name);
    formData.append('session', session);
    const response = yield call(apiClient.post, 'ocr-service/api/v2/scan/detect-boundaries', formData, {});

    if (response.error) {
        yield put(actions['detectBoundariesFailed'](response.detail));
        yield call(notifyError, response.detail);
    } else {
        yield put(actions['detectBoundariesSuccess'](response));
    }
}

function* useImage({payload}) {
    const {session, corners} = payload;
    const response = yield call(apiClient.post, `ocr-service/api/v2/scan/detect-boundaries/${session}`, {corners}, {});
    if (response.error) {
        yield put(actions['useImageFailed'](response.detail));
        yield call(notifyError, response.detail);
    } else {
        yield put(actions['useImageSuccess'](response.data));
    }
}

function* saveTemplate() {
    const state = yield select(getTemplateState);
    const {
        templateName, description, removeAccent, fullText, detectQR, detectSignature,
        detectMRZ, skipImageProcessing, scannedDocument, pages,
    } = state;
    const data = {
        title: templateName,
        description,
        removeAccent,
        fullText,
        detectQRCode: detectQR,
        detectSignature,
        detectMRZ,
        detectInvoice: true,
        skipProcessing: skipImageProcessing,
        isScanned: scannedDocument,
        pages,
    };

    const response = yield call(apiClient.create, `api/v2/templates`, data);

    if (response.error) {
        yield put(actions['saveTemplateFailed'](response.detail));
        yield call(notifyError, response.detail);
    } else {
        yield put(actions['saveTemplateSuccess'](response.data));
        yield call(notifySuccess, 'Template has been saved');
    }
}

function* setGeneralInformation({payload}) {
    const keys = Object.keys(payload);
    const values = {};
    const state = yield select(getTemplateState);

    keys.filter(k => k !== 'name').forEach(k => values[k] = payload[k]);

    const hasDefinedValues = [
        state.pages[state.currentPage].fields,
        state.fullText,
        state.detectMRZ,
        state.detectSignature,
        state.detectQR,
        values['fullText'],
        values['detectMRZ'],
        values['detectQR'],
        values['detectSignature'],
    ].some(k => !!k);

    const enableSaveAction = (gte(state.templateName.length, TEMPLATE_NAME_MIN_CHARS) ||
        gte(values['templateName'], TEMPLATE_NAME_MIN_CHARS)) && hasDefinedValues;

    yield put(actions['setGeneralInformationSuccess']({values, enableSaveAction}));
}

function* addField({payload}) {
    const state = yield select(getTemplateState);
    const {fieldName} = payload;

    const keys = Object.keys(payload);
    const keywords = keys.filter(k => k.startsWith('startKeyword'));
    const regexes = keys.filter(k => k.startsWith('regex'));
    let steps = [];

    if (keywords.length > 0 || regexes.length > 0) {
        const rules = [];
        let type = ANNOTATION_TYPE_KEYWORD;

        if (keywords.length > 0) {
            keywords.forEach(k => {
                const endKeyword = `endKeyword_${k.split('_')[1]}`;
                const inputKeyword = `item_${k.split('_')[1]}`;
                rules.push({start: payload[k], end: payload[endKeyword] || '', item: payload[inputKeyword]});
            });
        } else {
            type = ANNOTATION_TYPE_REGEX;
            regexes.forEach(k => {
                const inputKeyword = `item_${k.split('_')[1]}`;
                rules.push({value: payload[k], item: payload[inputKeyword]});
            })
        }

        steps.push({
            id: shortid.generate(),
            alternatives: [{id: shortid.generate(), type, rules,}]
        });
    }

    const field = {id: shortid.generate(), name: fieldName, steps,};

    const currentPage = {...state.pages[state.currentPage]};
    currentPage.fields.push(field);

    const pages = {...state.pages};
    pages[state.currentPage] = currentPage;

    yield put(actions['addFieldSuccess']({pages, field}));
}

function* addStep({payload}) {
    const state = yield select(getTemplateState);
    const {type, rules, input} = payload;
    const pages = {...state.pages};
    let disableTextLocation = false;
    let selectedField = null;
    let locationMap = {...state.locationMap};

    const alternative = {
        id: shortid.generate(),
        type,
        rules: [...rules] || [],
        input,
    };

    pages[state.currentPage].fields = pages[state.currentPage].fields.map(f => {
        const field = {...f};

        if (equals(field.id, state.selectedField.id)) {
            let shouldAddStep = true;
            const newStep = {id: shortid.generate(), alternatives: [alternative],};

            if (type === ANNOTATION_TYPE_LOCATION) {
                disableTextLocation = true;
                // If the location step has already existed
                if (field.steps.length > 0) {
                    field.steps[0].alternatives = field.steps[0].alternatives.map(a => {
                        if (a.type === ANNOTATION_TYPE_LOCATION) {
                            shouldAddStep = false;
                            locationMap[field.id] = {...alternative};
                            return alternative;
                        }
                        return a;
                    });
                }
            }

            if (shouldAddStep) {
                field.steps.push(newStep);
            }

            selectedField = field;
        }

        return field;
    });

    yield put(actions['addStepSuccess']({pages, disableTextLocation, selectedField, locationMap}));
}

function* deleteStep({payload}) {
    const state = yield select(getTemplateState);
    const {row, step} = payload;
    const selectedField = {...state.selectedField};
    selectedField.steps = state.selectedField.steps.map(s => {
        if (s.id === row) {
            s.alternatives = s.alternatives.filter(alt => alt.id !== step);
        }
        return step;
    });
    selectedField.steps = selectedField.steps.filter(s => s.alternatives.length > 0);
    yield put(actions['deleteStepSuccess']({selectedField}));
}

function* loadTemplate({payload}) {
    const {templateResourceId} = payload;
    const response = yield call(apiClient.get, 'api/v2/templates', templateResourceId);
    if (response.error) {
        yield put(actions['loadTemplateFailed'](response.detail));
        yield call(notifyError, response.detail);
    } else {
        yield put(actions['loadTemplateSuccess'](response));
    }
}


const buildRules = (payload) => {
    const {type} = payload;
    let rules = [];
    if (equals(type, ANNOTATION_TYPE_KEYWORD)) {
        rules = [{
            start: payload['startKeyword_0'],
            end: payload['endKeyword_0'],
            item: payload['item_0']
        }];
    } else {
        rules = [{value: payload['regex_0'], item: payload['item_0']}];
    }

    return rules;
}

function* addAlternativeStep({payload}) {
    const state = yield select(getTemplateState);
    const pages = {...state.pages};
    const {type} = payload;


    pages[state.currentPage].fields = pages[state.currentPage].fields.map(f => {
        const field = {...f};
        if (equals(field.id, state.selectedField.id)) {
            field.steps = field.steps.map(s => {
                if (equals(s.id, payload.step)) {
                    const step = {...s};
                    let rules = buildRules(payload);
                    let alternativeExisted = false;

                    step.alternatives.forEach(a => {
                        if (equals(a.type, type)) {
                            a.rules = concat(a.rules, rules);
                            alternativeExisted = true;
                        }
                    });

                    if (!alternativeExisted) {
                        const nodeIndex = step.alternatives.findIndex(a => a.id === state.selectedNode);
                        step.alternatives = insert(nodeIndex + 1, {
                            id: shortid.generate(),
                            type,
                            rules,
                            input: null
                        }, step.alternatives);
                    }
                    return step;
                }
                return s;
            });
        }
        return field;
    })
    yield put(actions['addAlternativeStepSuccess']({pages}));
}

const templateSaga = createCoreSagas([
    {
        type: actionTypes['detectBoundaries'],
        callback: detectBoundaries,
    }, {
        type: actionTypes['useImage'],
        callback: useImage,
    }, {
        type: actionTypes['saveTemplate'],
        callback: saveTemplate,
    }, {
        type: actionTypes['setGeneralInformation'],
        callback: setGeneralInformation,
    }, {
        type: actionTypes['addField'],
        callback: addField,
    }, {
        type: actionTypes['addStep'],
        callback: addStep,
    }, {
        type: actionTypes['deleteStep'],
        callback: deleteStep,
    }, {
        type: actionTypes['loadTemplate'],
        callback: loadTemplate,
    }, {
        type: actionTypes['addAlternativeStep'],
        callback: addAlternativeStep,
    },
]);

export default templateSaga;
